import { useState, useEffect } from 'react';
import './index.css';

import type { AdminUser, TeacherListItem, AttendanceStatus, AppScreen } from './utils/types';

import SplashScreen          from './components/SplashScreen';
import LoginScreen            from './components/LoginScreen';
import HomeScreen             from './components/HomeScreen';
import TeacherPinDrawer       from './components/TeacherPinDrawer';
import CameraScreen           from './components/CameraScreen';
import PhotoPreview           from './components/PhotoPreview';
import PWAInstallBanner       from './components/PWAInstallBanner';
import AlreadyMarkedDrawer    from './components/AlreadyMarkedDrawer';
import { logout, getAllTeachers, getAttendance } from './Api/auth';
import { formatMarkedTime } from './utils/time';

const ADMIN_KEY    = 'vav_att_admin';
const TEACHERS_KEY = 'vav_att_teachers';

function loadAdmin(): AdminUser | null {
  try { const s = localStorage.getItem(ADMIN_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
}

function getTodayOverlay(): Record<string, { todayStatus: AttendanceStatus; markedAt?: string }> {
  try {
    const today = new Date().toDateString();
    if (localStorage.getItem('vav_att_date') !== today) {
      localStorage.removeItem(TEACHERS_KEY);
      localStorage.setItem('vav_att_date', today);
      return {};
    }
    const s = localStorage.getItem(TEACHERS_KEY);
    if (!s) return {};
    const saved: TeacherListItem[] = JSON.parse(s);
    return Object.fromEntries(saved.map(t => [t.id, { todayStatus: t.todayStatus, markedAt: t.markedAt }]));
  } catch { return {}; }
}

function App() {
  const [splash,  setSplash]  = useState(true);
  const [admin,   setAdmin]   = useState<AdminUser | null>(loadAdmin);
  const [screen,  setScreen]  = useState<Omit<AppScreen, 'splash'> | 'home'>('home');
  const [teachers, setTeachers] = useState<TeacherListItem[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersError, setTeachersError]   = useState<string | null>(null);
  const [fetchTick, setFetchTick] = useState(0);

  const [selected,     setSelected]     = useState<TeacherListItem | null>(null);
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [alreadyOpen,  setAlreadyOpen]  = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [captureTime,   setCaptureTime]   = useState<Date | null>(null);

  useEffect(() => {
    if (!admin) { setTeachers([]); return; }
    setTeachersLoading(true);
    setTeachersError(null);
    const todayISO = new Date().toISOString().slice(0, 10);
    Promise.allSettled([getAllTeachers(), getAttendance({ date: todayISO })])
      .then(([teachersResult, attendanceResult]) => {
        if (teachersResult.status === 'rejected') {
          throw new Error(teachersResult.reason?.message ?? 'Failed to load teachers.');
        }
        const fetched = teachersResult.value;
        const records = attendanceResult.status === 'fulfilled' ? attendanceResult.value : [];
        const apiOverlay: Record<string, { todayStatus: AttendanceStatus; markedAt?: string }> = {};
        for (const r of records) {
          apiOverlay[String(r.teacher_id)] = {
            todayStatus: r.status,
            markedAt: formatMarkedTime(r.marked_at_device, r.created_at),
          };
        }
        const localOverlay = getTodayOverlay();
        setTeachers(fetched.map(t => ({
          ...t,
          ...(localOverlay[t.id] ?? {}),
          ...(apiOverlay[t.id] ?? {}),
        })));
      })
      .catch(err => setTeachersError(err.message ?? 'Failed to load teachers.'))
      .finally(() => setTeachersLoading(false));
  }, [admin, fetchTick]);

  const handleSplashDone = () => setSplash(false);

  const handleLogin = (user: AdminUser) => {
    setAdmin(user);
    localStorage.setItem(ADMIN_KEY, JSON.stringify(user));
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    localStorage.removeItem(ADMIN_KEY);
    setAdmin(null);
    setScreen('home');
  };

  const handleTeacherSelect = (teacher: TeacherListItem) => {
    setSelected(teacher);
    if (teacher.todayStatus !== null) {
      setAlreadyOpen(true);
    } else {
      setDrawerOpen(true);
    }
  };

  const handlePinSuccess = () => {
    setDrawerOpen(false);
    setScreen('camera' as any);
  };

  const handlePinCancel = () => {
    setDrawerOpen(false);
    setSelected(null);
  };

  const handleAlreadyClose = () => {
    setAlreadyOpen(false);
    setSelected(null);
  };

  const handleAlreadyUpdate = () => {
    setAlreadyOpen(false);
    setDrawerOpen(true);
  };

  const handlePhotoCapture = (photo: string, time: Date) => {
    setCapturedPhoto(photo);
    setCaptureTime(time);
    setScreen('preview' as any);
  };

  const handleCameraBack = () => {
    setScreen('home' as any);
    setSelected(null);
  };

  const handleRetake = () => {
    setCapturedPhoto(null);
    setCaptureTime(null);
    setScreen('camera' as any);
  };

  const handleAttendanceSubmit = (updated: TeacherListItem) => {
    const next = teachers.map(t => t.id === updated.id ? updated : t);
    setTeachers(next);
    localStorage.setItem(TEACHERS_KEY, JSON.stringify(next));
    localStorage.setItem('vav_att_date', new Date().toDateString());
    setCapturedPhoto(null);
    setCaptureTime(null);
    setSelected(null);
    setScreen('home' as any);
  };

  if (splash) return <SplashScreen onDone={handleSplashDone} />;
  if (!admin)  return <><LoginScreen onLogin={handleLogin} /><PWAInstallBanner /></>;

  const currentScreen = screen as string;

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      height: '100dvh', overflow: 'hidden', position: 'fixed', width: '100%', top: 0, left: 0,
    }}>
      {currentScreen !== 'camera' && currentScreen !== 'preview' && (
        <>
          <HomeScreen
            adminUser={admin}
            teachers={teachers}
            onTeacherSelect={handleTeacherSelect}
            onLogout={handleLogout}
          />
          {teachersLoading && (
            <div style={{
              position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.75)', zIndex: 50,
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#555' }}>Loading teachers…</div>
            </div>
          )}
          {!teachersLoading && teachersError && (
            <div style={{
              position: 'fixed', bottom: 24, left: 16, right: 16, background: '#fff3f3',
              border: '1px solid #ffb3b3', borderRadius: 12, padding: '12px 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 50,
            }}>
              <span style={{ fontSize: 13, color: '#c00', fontWeight: 500 }}>{teachersError}</span>
              <button onClick={() => { setTeachersError(null); setFetchTick(n => n + 1); }} style={{
                background: 'none', border: 'none', fontSize: 13, fontWeight: 700, color: '#c00', cursor: 'pointer',
              }}>Retry</button>
            </div>
          )}
        </>
      )}

      {drawerOpen && selected && (
        <TeacherPinDrawer
          teacher={selected}
          onSuccess={handlePinSuccess}
          onCancel={handlePinCancel}
        />
      )}

      {currentScreen === 'camera' && selected && (
        <CameraScreen
          teacher={selected}
          onCapture={handlePhotoCapture}
          onBack={handleCameraBack}
        />
      )}

      {currentScreen === 'preview' && selected && capturedPhoto && captureTime && (
        <PhotoPreview
          teacher={selected}
          photo={capturedPhoto}
          captureTime={captureTime}
          onRetake={handleRetake}
          onSubmit={handleAttendanceSubmit}
        />
      )}

      {alreadyOpen && selected && (
        <AlreadyMarkedDrawer
          teacher={selected}
          onClose={handleAlreadyClose}
          onUpdate={handleAlreadyUpdate}
        />
      )}

      <PWAInstallBanner />
    </div>
  );
}

export default App;
